import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { GraduationCap, Trash2, BarChart3 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { week: 1, title: "", subtitle: "", summary: "", hero_image: "", estimated_minutes: 0, published: false, discussion_prompt: "", badge: { id: "", label: "", icon: "shield" }, then_vs_now: { then: "", now: "", same: "" }, lessons: [] };

export function AcademyAdmin() {
  const [mods, setMods] = useState([]);
  const [prog, setProg] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [lessonsJson, setLessonsJson] = useState("[]");

  const load = useCallback(() => {
    api.get("/admin/academy/modules").then(({ data }) => setMods(data)).catch(() => {});
    api.get("/admin/academy/progress").then(({ data }) => setProg(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const editMod = (m) => {
    setF({ ...empty, ...m, badge: m.badge || empty.badge, then_vs_now: m.then_vs_now || empty.then_vs_now });
    setLessonsJson(JSON.stringify(m.lessons || [], null, 2));
    setOpen(true);
  };
  const newMod = () => { setF(empty); setLessonsJson("[]"); setOpen(true); };

  const save = async () => {
    let lessons;
    try { lessons = JSON.parse(lessonsJson || "[]"); if (!Array.isArray(lessons)) throw new Error(); }
    catch { toast.error("Lessons must be valid JSON array"); return; }
    try {
      await api.post("/admin/academy/modules", { ...f, week: Number(f.week), estimated_minutes: Number(f.estimated_minutes) || 0, lessons });
      toast.success("Module saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this module?")) return;
    try { await api.delete(`/admin/academy/modules/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="card-surface rounded-md p-5 mb-6" data-testid="academy-admin">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><GraduationCap className="w-4 h-4 text-gold" /><h3 className="font-display font-semibold text-white">Detective Academy</h3></div>
        <Button onClick={newMod} className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm text-sm" data-testid="add-module-btn">Add module</Button>
      </div>
      <p className="text-xs text-white/50 mb-4">12-week dripped course. Edit each module's content and lessons (JSON). Publish when ready — learners see published modules on their weekly schedule.</p>

      {prog && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="academy-progress">
          <div className="rounded border border-white/10 p-3 text-center"><div className="font-display text-xl font-bold text-gold">{prog.total_learners}</div><div className="label-mono text-white/40">Learners</div></div>
          <div className="rounded border border-white/10 p-3 text-center"><div className="font-display text-xl font-bold text-gold">{prog.certified_count}</div><div className="label-mono text-white/40">Certified</div></div>
          <div className="rounded border border-white/10 p-3 text-center"><div className="font-display text-xl font-bold text-gold">{prog.total_published_modules}</div><div className="label-mono text-white/40">Live modules</div></div>
        </div>
      )}

      <div className="space-y-1.5">
        {mods.map((m) => (
          <div key={m.module_id} className="flex items-center gap-3 rounded border border-white/10 px-3 py-2" data-testid={`admin-module-${m.week}`}>
            <span className="w-7 h-7 rounded bg-white/5 flex items-center justify-center font-display font-bold text-white/70 text-sm shrink-0">{m.week}</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white/85 truncate">{m.title}</div>
              <div className="label-mono text-white/40">{(m.lessons || []).length} lessons · {m.published ? <span className="text-gold">Published</span> : "Draft"}</div>
            </div>
            <button onClick={() => editMod(m)} className="text-xs text-white/60 hover:text-gold" data-testid={`edit-module-${m.week}`}>Edit</button>
            <button onClick={() => del(m.module_id)} className="text-red-400/70 hover:text-red-400" data-testid={`delete-module-${m.week}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      {prog?.per_module && (
        <div className="mt-4">
          <div className="flex items-center gap-1.5 label-mono text-white/40 mb-2"><BarChart3 className="w-3.5 h-3.5" /> Completions per module</div>
          <div className="space-y-1">
            {prog.per_module.map((p) => (
              <div key={p.week} className="flex items-center gap-2 text-xs">
                <span className="w-6 text-white/40">W{p.week}</span>
                <span className="flex-1 text-white/60 truncate">{p.title}</span>
                <span className="text-gold">{p.completions}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-charcoal border-white/15 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{f.module_id ? "Edit module" : "New module"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2">
              <Input type="number" min="1" max="12" value={f.week} onChange={(e) => setF({ ...f, week: e.target.value })} placeholder="Week" className="bg-charcoal border-white/15" data-testid="module-week" />
              <Input type="number" value={f.estimated_minutes} onChange={(e) => setF({ ...f, estimated_minutes: e.target.value })} placeholder="Minutes" className="bg-charcoal border-white/15 col-span-1" />
              <label className="col-span-2 flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} className="accent-gold" data-testid="module-published" /> Published (live to learners)</label>
            </div>
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className="bg-charcoal border-white/15" data-testid="module-title" />
            <Input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} placeholder="Subtitle" className="bg-charcoal border-white/15" />
            <Textarea value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} rows={2} placeholder="Summary" className="bg-charcoal border-white/15" />
            <Input value={f.hero_image} onChange={(e) => setF({ ...f, hero_image: e.target.value })} placeholder="Hero image URL" className="bg-charcoal border-white/15" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={f.badge?.label || ""} onChange={(e) => setF({ ...f, badge: { ...f.badge, label: e.target.value, id: f.badge?.id || `badge_w${f.week}` } })} placeholder="Badge label" className="bg-charcoal border-white/15" />
              <Input value={f.badge?.icon || ""} onChange={(e) => setF({ ...f, badge: { ...f.badge, icon: e.target.value } })} placeholder="Badge icon (shield, scale, search…)" className="bg-charcoal border-white/15" />
            </div>
            <Textarea value={f.discussion_prompt} onChange={(e) => setF({ ...f, discussion_prompt: e.target.value })} rows={2} placeholder="Discussion prompt (case-analysis homework)" className="bg-charcoal border-white/15" />
            <div className="label-mono text-white/40 pt-1">Then vs Now</div>
            <Input value={f.then_vs_now?.then || ""} onChange={(e) => setF({ ...f, then_vs_now: { ...f.then_vs_now, then: e.target.value } })} placeholder="Then (decades ago)" className="bg-charcoal border-white/15" />
            <Input value={f.then_vs_now?.now || ""} onChange={(e) => setF({ ...f, then_vs_now: { ...f.then_vs_now, now: e.target.value } })} placeholder="Now (modern)" className="bg-charcoal border-white/15" />
            <Input value={f.then_vs_now?.same || ""} onChange={(e) => setF({ ...f, then_vs_now: { ...f.then_vs_now, same: e.target.value } })} placeholder="What stays the same" className="bg-charcoal border-white/15" />
            <div className="label-mono text-white/40 pt-1">Lessons (JSON array — reading / quiz / scenario / hotspot)</div>
            <Textarea value={lessonsJson} onChange={(e) => setLessonsJson(e.target.value)} rows={10} className="bg-charcoal border-white/15 font-mono text-xs" data-testid="module-lessons-json" />
          </div>
          <DialogFooter><Button onClick={save} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-module-btn">Save module</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
